/**
 * GuestChatWidget Component
 *
 * Floating chat widget for guest users on the landing page.
 * Based on the existing FloatingChat design for consistency.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spinner } from 'reactstrap';

// Team avatar images - use same as FloatingChat
import avatar1 from '../../../assets/images/users/avatar-2.jpg';
import avatar2 from '../../../assets/images/users/avatar-3.jpg';
import avatar3 from '../../../assets/images/users/avatar-5.jpg';

// Header background
import headerBg from '../../../assets/images/auth-one-bg.jpg';


const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface Message {
  id: number;
  message: string;
  sender_type: 'user' | 'agent' | 'ai' | 'admin';
  created_at: string;
  senderName?: string;
  message_type?: 'text' | 'topic' | 'image' | 'file';
}

interface ChatSession {
  roomId: string;
  guestToken: string;
  guestName: string;
  guestEmail: string;
}

// Support topics
const SUPPORT_TOPICS = [
  { icon: '📦', label: 'Product Question', value: 'Product Question' },
  { icon: '💳', label: 'Pricing & Plans', value: 'Pricing & Plans' },
  { icon: '🚀', label: 'Getting Started', value: 'Getting Started' },
  { icon: '🔧', label: 'Technical Support', value: 'Technical Support' },
  { icon: '❓', label: 'General Inquiry', value: 'General Inquiry' }
];

type TabType = 'home' | 'chat' | 'selectTopic';

const GuestChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('home');
  const [session, setSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Form fields
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [showForm, setShowForm] = useState(true);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastMessageIdRef = useRef<number>(0);
  const sentMessageIdsRef = useRef<Set<number>>(new Set()); // Track sent message IDs to prevent duplicates
  const isSendingRef = useRef<boolean>(false); // Prevent double-send
  const isOpenRef = useRef<boolean>(false); // Track open state for notifications

  // Keep ref in sync with state
  useEffect(() => {
    isOpenRef.current = isOpen;
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  // Initialize notification sound
  useEffect(() => {
    let audioContext: AudioContext | null = null;

    const createNotificationSound = async () => {
      try {
        // Create AudioContext lazily (browser autoplay policy)
        if (!audioContext) {
          audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        }

        // Resume if suspended (browser autoplay policy)
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        // Pleasant notification tone (like a soft "ding")
        oscillator.frequency.setValueAtTime(830, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(660, audioContext.currentTime + 0.08);

        gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.25);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.25);
      } catch (e) {
        // Ignore audio errors
      }
    };

    // Store the function reference
    (window as any).__playNotificationSound = createNotificationSound;

    return () => {
      delete (window as any).__playNotificationSound;
      if (audioContext) {
        audioContext.close();
      }
    };
  }, []);

  const playNotificationSound = () => {
    if ((window as any).__playNotificationSound) {
      (window as any).__playNotificationSound();
    }
  };

  // Team avatars
  const teamAvatars = [
    { name: 'Sarah', image: avatar1 },
    { name: 'James', image: avatar2 },
    { name: 'Emma', image: avatar3 }
  ];

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Load session from localStorage
  useEffect(() => {
    const savedSession = localStorage.getItem('guestChatSession');
    if (savedSession) {
      try {
        const parsed = JSON.parse(savedSession);
        setSession(parsed);
        setGuestName(parsed.guestName);
        setGuestEmail(parsed.guestEmail);
        setShowForm(false);
      } catch (e) {
        localStorage.removeItem('guestChatSession');
      }
    }
  }, []);

  // Connect WebSocket when session is available
  useEffect(() => {
    if (session && isOpen && activeTab === 'chat') {
      connectWebSocket();
      loadMessages();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, isOpen, activeTab]);

  const connectWebSocket = () => {
    if (!session) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = API_URL.replace(/^https?:\/\//, '');
    const wsUrl = `${protocol}//${host}/api/apps/chat/guest/ws/${session.roomId}?guest_token=${session.guestToken}`;

    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('Guest WebSocket connected');
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_message') {
            const msg = data.message;

            // Skip messages we already sent (tracked by ID)
            if (sentMessageIdsRef.current.has(msg.id)) {
              return;
            }

            // Skip topic messages (only admin sees them)
            if (msg.messageType === 'topic') {
              return;
            }

            // Skip user messages while we're sending (prevents race condition)
            // Also skip auto-response messages during send (they come in REST response)
            if (isSendingRef.current) {
              if (msg.senderType === 'user' || msg.senderId === 'system_auto') {
                return;
              }
            }

            setMessages((prev) => {
              // Final duplicate check by ID
              if (prev.some((m) => m.id === msg.id)) return prev;
              return [...prev, {
                id: msg.id,
                message: msg.message,
                sender_type: msg.senderType,
                created_at: msg.createdAt,
                senderName: msg.senderName,
                message_type: msg.messageType
              }];
            });

            // Play notification sound and update badge for agent/admin messages
            if (msg.senderType === 'agent' || msg.senderType === 'admin') {
              playNotificationSound();
              // Increment unread count if chat is closed
              if (!isOpenRef.current) {
                setUnreadCount((prev) => prev + 1);
              }
            }
          }
        } catch (e) {
          console.error('Failed to parse WS message:', e);
        }
      };

      wsRef.current.onclose = () => {
        console.log('Guest WebSocket disconnected');
        startPolling();
      };

      wsRef.current.onerror = () => {
        startPolling();
      };
    } catch (e) {
      startPolling();
    }
  };

  const startPolling = () => {
    if (pollIntervalRef.current) return;
    pollIntervalRef.current = setInterval(() => {
      if (session) pollMessages();
    }, 3000);
  };

  const pollMessages = async () => {
    if (!session) return;
    try {
      const response = await fetch(
        `${API_URL}/api/apps/chat/guest/${session.roomId}/messages?guest_token=${session.guestToken}&after_id=${lastMessageIdRef.current}`
      );
      if (response.status === 401) { handleSessionExpired(); return; }
      if (response.ok) {
        const data = await response.json();
        if (data.messages && data.messages.length > 0) {
          // Filter out topic messages - they are only for admin
          const visibleMsgs = data.messages.filter((m: Message) => m.message_type !== 'topic');
          setMessages((prev) => {
            const newMsgs = visibleMsgs.filter((m: Message) => !prev.some((p) => p.id === m.id));
            if (newMsgs.length > 0) {
              lastMessageIdRef.current = Math.max(...data.messages.map((m: Message) => m.id));
              // Play sound and update badge for new agent/admin messages
              const agentMsgs = newMsgs.filter((m: Message) => m.sender_type === 'agent' || m.sender_type === 'admin');
              if (agentMsgs.length > 0) {
                playNotificationSound();
                // Increment unread count if chat is closed
                if (!isOpenRef.current) {
                  setUnreadCount((prev) => prev + agentMsgs.length);
                }
              }
              return [...prev, ...newMsgs];
            }
            return prev;
          });
        }
      }
    } catch (e) {
      console.error('Polling error:', e);
    }
  };

  const loadMessages = async () => {
    if (!session) return;
    try {
      const response = await fetch(
        `${API_URL}/api/apps/chat/guest/${session.roomId}/messages?guest_token=${session.guestToken}`
      );
      if (response.status === 401) { handleSessionExpired(); return; }
      if (response.ok) {
        const data = await response.json();
        if (data.messages) {
          // Filter out topic messages - they are only for admin
          const visibleMessages = data.messages.filter((m: Message) => m.message_type !== 'topic');
          setMessages(visibleMessages);
          if (data.messages.length > 0) {
            lastMessageIdRef.current = Math.max(...data.messages.map((m: Message) => m.id));
          }
        }
      }
    } catch (e) {
      console.error('Failed to load messages:', e);
    }
  };

  const handleStartChat = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!guestName.trim() || !guestEmail.trim()) {
      setError('Please enter your name and email');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/apps/chat/guest/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: guestEmail.trim(), name: guestName.trim() })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to start chat');
      }

      const data = await response.json();
      const newSession: ChatSession = {
        roomId: data.room_id,
        guestToken: data.guest_token,
        guestName: guestName.trim(),
        guestEmail: guestEmail.trim()
      };

      setSession(newSession);
      localStorage.setItem('guestChatSession', JSON.stringify(newSession));
      setShowForm(false);

      if (data.messages && data.messages.length > 0) {
        setMessages(data.messages);
        lastMessageIdRef.current = Math.max(...data.messages.map((m: Message) => m.id));
        setActiveTab('chat');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to start chat');
    } finally {
      setLoading(false);
    }
  };

  const createNewConversation = async (topic: string) => {
    if (!session) return;

    setLoading(true);
    try {
      // Send topic selection to admin (not shown to user, no auto-response)
      const response = await fetch(`${API_URL}/api/apps/chat/guest/${session.roomId}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: `📋 Topic: ${topic}`,
          guest_token: session.guestToken,
          is_topic_selection: true  // This won't trigger auto-response
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Track topic message ID to prevent it from showing via WebSocket
        sentMessageIdsRef.current.add(data.id);
      }

      // Go directly to chat without loading the topic message
      setActiveTab('chat');
    } catch (err) {
      console.error('Error starting conversation:', err);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !session || sending || isSendingRef.current) {
      return;
    }

    // Use ref for immediate lock (state updates are async)
    isSendingRef.current = true;

    const messageText = newMessage.trim();
    setNewMessage('');
    setSending(true);

    // NO optimistic update - wait for REST response to prevent duplicates
    try {
      const response = await fetch(`${API_URL}/api/apps/chat/guest/${session.roomId}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: messageText, guest_token: session.guestToken })
      });

      if (response.status === 401) { handleSessionExpired(); return; }
      if (!response.ok) throw new Error('Failed to send message');

      const data = await response.json();

      // Track this message ID BEFORE adding to messages (prevents WebSocket duplicate)
      sentMessageIdsRef.current.add(data.id);

      // Add the sent message to UI
      const sentMsg: Message = {
        id: data.id,
        message: data.message,
        sender_type: data.sender_type,
        created_at: data.created_at
      };
      setMessages((prev) => {
        // Double-check it's not already there
        if (prev.some((m) => m.id === data.id)) return prev;
        return [...prev, sentMsg];
      });
      lastMessageIdRef.current = Math.max(lastMessageIdRef.current, data.id);

      // Add auto-response if present (first message gets auto-response)
      // Show it after a short delay to feel more natural
      if (data.auto_response) {
        // Track auto-response ID immediately to prevent WebSocket duplicate
        sentMessageIdsRef.current.add(data.auto_response.id);

        // Delay the auto-response appearance by 2-3 seconds
        setTimeout(() => {
          const autoMsg: Message = {
            id: data.auto_response.id,
            message: data.auto_response.message,
            sender_type: data.auto_response.sender_type,
            created_at: data.auto_response.created_at,
            senderName: 'Coderom Support'
          };
          setMessages((prev) => {
            if (prev.some((m) => m.id === data.auto_response.id)) return prev;
            return [...prev, autoMsg];
          });
          lastMessageIdRef.current = Math.max(lastMessageIdRef.current, data.auto_response.id);

          // Play notification sound
          playNotificationSound();
        }, 2000 + Math.random() * 1000); // 2-3 seconds random delay
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      // Show error to user - restore the message text
      setNewMessage(messageText);
    } finally {
      setSending(false);
      isSendingRef.current = false;
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // [GUVENLIK-2b] Misafir token'i artik sure sinirli ve imzali; backend 401 donunce oturum silinir ve form yeniden gosterilir.
  // Ayni ad/e-posta ile baslatmak acik odayi ve yeni token'i geri getirir (mesajlar kaybolmaz).
  const handleSessionExpired = () => {
    if (wsRef.current) wsRef.current.close();
    if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
    localStorage.removeItem('guestChatSession');
    setSession(null);
    setShowForm(true);
    setActiveTab('home');
    setError('Your chat session has expired. Please enter your name and email to continue.');
  };

  const handleNewConversation = () => {
    localStorage.removeItem('guestChatSession');
    setSession(null);
    setMessages([]);
    setShowForm(true);
    setGuestName('');
    setGuestEmail('');
    setActiveTab('home');
    if (wsRef.current) wsRef.current.close();
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
  };

  const formatTime = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getAdminAvatarForSender = (senderName: string) => {
    const lowerName = (senderName || '').toLowerCase();
    if (lowerName.includes('james')) return avatar2;
    if (lowerName.includes('emma')) return avatar3;
    return avatar1;
  };

  // Simple markdown formatter for auto-response messages
  const formatMessage = (text: string) => {
    if (!text) return null;

    // Split by lines and process
    const lines = text.split('\n');

    return lines.map((line, lineIdx) => {
      // Process bold text (**text**)
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      const formattedParts = parts.map((part, partIdx) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={partIdx}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });

      return (
        <span key={lineIdx}>
          {formattedParts}
          {lineIdx < lines.length - 1 && <br />}
        </span>
      );
    });
  };

  return (
    <>
      {/* Floating Button - same as FloatingChat */}
      {!isOpen && (
        <div
          onClick={() => {
            setIsOpen(true);
            setUnreadCount(0);
          }}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#405189',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 9999,
            transition: 'transform 0.2s'
          }}
          className="floating-chat-btn"
        >
          <i className="ri-message-3-line" style={{ fontSize: '24px', color: 'white' }}></i>
          {/* Notification Badge */}
          {unreadCount > 0 && (
            <div
              style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                minWidth: '22px',
                height: '22px',
                borderRadius: '11px',
                backgroundColor: '#f06548',
                color: 'white',
                fontSize: '12px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 6px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                animation: 'pulse 2s infinite'
              }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </div>
          )}
        </div>
      )}

      {/* Chat Window - same dimensions and style as FloatingChat */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            width: '380px',
            height: '550px',
            zIndex: 9999,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            borderRadius: '16px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'white'
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: activeTab === 'chat' ? '12px 16px' : '24px 16px',
              color: activeTab === 'chat' ? '#212529' : 'white',
              position: 'relative',
              overflow: activeTab === 'chat' ? 'visible' : 'hidden',
              backgroundColor: activeTab === 'chat' ? '#fff' : 'transparent',
              borderBottom: activeTab === 'chat' ? '1px solid #e9ebec' : 'none'
            }}
          >
            {/* Background - only when not in chat */}
            {activeTab !== 'chat' && (
              <>
                <div
                  style={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundImage: `url(${headerBg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'blur(2px)',
                    transform: 'scale(1.1)',
                    zIndex: 0
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: 'linear-gradient(135deg, rgba(64, 81, 137, 0.85) 0%, rgba(10, 179, 156, 0.75) 100%)',
                    zIndex: 1
                  }}
                />
              </>
            )}

            {/* Close button for non-chat tabs */}
            {activeTab !== 'chat' && (
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: 'transparent',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '20px',
                  padding: '8px',
                  zIndex: 10
                }}
              >
                <i className="ri-close-line"></i>
              </button>
            )}

            {activeTab === 'chat' ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    onClick={() => setActiveTab('home')}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#495057',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '4px'
                    }}
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  <div style={{ display: 'flex', marginLeft: '4px' }}>
                    {teamAvatars.map((avatar, idx) => (
                      <img
                        key={idx}
                        src={avatar.image}
                        alt={avatar.name}
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          border: '2px solid white',
                          marginLeft: idx > 0 ? '-8px' : 0,
                          objectFit: 'cover'
                        }}
                      />
                    ))}
                  </div>
                  <div style={{ marginLeft: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <img src="/icon.png" alt="CodeRom" style={{ height: '20px', width: 'auto' }} />
                    </div>
                    <div style={{ fontSize: '12px', color: '#0ab39c', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                      <i className="ri-time-line" style={{ fontSize: '12px' }}></i>
                      Under 30 minutes
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#495057',
                    cursor: 'pointer',
                    fontSize: '18px',
                    padding: '4px'
                  }}
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            ) : (
              <div style={{ position: 'relative', zIndex: 2 }}>
                {/* Team avatars */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', marginBottom: '12px' }}>
                  {teamAvatars.map((avatar, idx) => (
                    <img
                      key={idx}
                      src={avatar.image}
                      alt={avatar.name}
                      style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '50%',
                        border: '3px solid white',
                        marginLeft: idx > 0 ? '-10px' : 0,
                        objectFit: 'cover',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    />
                  ))}
                </div>

                {/* Welcome text */}
                <div style={{ textAlign: 'center' }}>
                  <h3 style={{ margin: 0, fontWeight: 700, color: 'white', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
                    {session ? `Hi ${session.guestName}! 👋` : 'Hi there! 👋'}
                  </h3>
                  <p style={{ margin: '6px 0 0', opacity: 0.95, fontSize: '15px', color: 'white' }}>
                    How can we help you?
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Home Tab - Guest Form or Topic Selection */}
            {activeTab === 'home' && (
              <div style={{ flex: 1, padding: '16px', overflowY: 'auto' }}>
                {showForm ? (
                  /* Guest Registration Form */
                  <form onSubmit={handleStartChat}>
                    <h5 style={{ color: '#212529', marginBottom: '8px', fontWeight: 600 }}>
                      Let's get started
                    </h5>
                    <p style={{ color: '#878a99', fontSize: '13px', marginBottom: '20px' }}>
                      Enter your details to chat with our support team
                    </p>

                    {error && (
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '8px',
                        color: '#dc2626',
                        fontSize: '14px',
                        marginBottom: '16px'
                      }}>
                        {error}
                      </div>
                    )}

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '6px' }}>
                        Name *
                      </label>
                      <input
                        type="text"
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                        placeholder="Your name"
                        required
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          border: '1px solid #e9ebec',
                          borderRadius: '8px',
                          fontSize: '14px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '6px' }}>
                        Email *
                      </label>
                      <input
                        type="email"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        placeholder="your@email.com"
                        required
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          border: '1px solid #e9ebec',
                          borderRadius: '8px',
                          fontSize: '14px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: loading ? '#9ca3af' : '#405189',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '15px',
                        fontWeight: 500,
                        cursor: loading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      {loading ? <Spinner size="sm" color="light" /> : null}
                      {loading ? 'Starting...' : 'Continue'}
                    </button>
                  </form>
                ) : (
                  /* Topic Selection */
                  <>
                    <h5 style={{ color: '#212529', marginBottom: '8px', fontWeight: 600 }}>
                      What can we help you with?
                    </h5>
                    <p style={{ color: '#878a99', fontSize: '13px', marginBottom: '20px' }}>
                      Select a topic to start the conversation
                    </p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {SUPPORT_TOPICS.map((topic) => (
                        <button
                          key={topic.value}
                          onClick={() => createNewConversation(topic.value)}
                          disabled={loading}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '14px 16px',
                            background: 'white',
                            border: '1px solid #e9ebec',
                            borderRadius: '10px',
                            cursor: loading ? 'wait' : 'pointer',
                            textAlign: 'left',
                            transition: 'all 0.2s ease',
                            opacity: loading ? 0.7 : 1
                          }}
                          onMouseOver={(e) => {
                            if (!loading) {
                              e.currentTarget.style.borderColor = '#405189';
                              e.currentTarget.style.backgroundColor = '#f8f9ff';
                            }
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.borderColor = '#e9ebec';
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                        >
                          <span style={{ fontSize: '24px' }}>{topic.icon}</span>
                          <span style={{ fontWeight: 500, color: '#212529', fontSize: '14px' }}>
                            {topic.label}
                          </span>
                          {loading && <Spinner size="sm" color="primary" style={{ marginLeft: 'auto' }} />}
                        </button>
                      ))}
                    </div>

                    {/* Direct chat option */}
                    <div
                      onClick={() => setActiveTab('chat')}
                      style={{
                        marginTop: '16px',
                        padding: '12px 16px',
                        backgroundColor: '#f3f6f9',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <i className="ri-message-2-line" style={{ color: '#405189' }}></i>
                      <span style={{ fontSize: '13px', color: '#495057' }}>
                        Or just start chatting directly
                      </span>
                      <i className="ri-arrow-right-s-line" style={{ marginLeft: 'auto', color: '#878a99' }}></i>
                    </div>

                    {/* New chat link */}
                    <button
                      onClick={handleNewConversation}
                      style={{
                        marginTop: '16px',
                        padding: '8px',
                        background: 'transparent',
                        border: 'none',
                        color: '#878a99',
                        fontSize: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        width: '100%'
                      }}
                    >
                      <i className="ri-refresh-line"></i>
                      Start as different user
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Chat Tab */}
            {activeTab === 'chat' && (
              <>
                <div
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '16px',
                    backgroundColor: '#f3f3f9'
                  }}
                >
                  {messages.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: '#878a99' }}>
                      <i className="ri-message-3-line" style={{ fontSize: '48px', marginBottom: '12px', display: 'block' }}></i>
                      <p>Start a conversation with our support team!</p>
                    </div>
                  ) : (
                    messages.map((msg, index) => (
                      <div
                        key={msg.id || index}
                        style={{
                          display: 'flex',
                          marginBottom: '12px',
                          justifyContent: msg.sender_type === 'user' ? 'flex-end' : 'flex-start'
                        }}
                      >
                        {/* Avatar for admin/agent messages */}
                        {msg.sender_type !== 'user' && (
                          <div style={{ marginRight: '8px', flexShrink: 0 }}>
                            <img
                              src={getAdminAvatarForSender(msg.senderName || '')}
                              alt={msg.senderName || 'Support'}
                              style={{
                                width: '28px',
                                height: '28px',
                                borderRadius: '50%',
                                objectFit: 'cover'
                              }}
                            />
                          </div>
                        )}
                        <div
                          style={{
                            maxWidth: '80%',
                            padding: '10px 14px',
                            borderRadius: msg.sender_type === 'user' ? '12px 12px 0 12px' : '12px 12px 12px 0',
                            backgroundColor: msg.sender_type === 'user' ? '#405189' : 'white',
                            color: msg.sender_type === 'user' ? 'white' : '#212529',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                          }}
                        >
                          <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                            {formatMessage(msg.message)}
                          </div>
                          <div style={{ fontSize: '10px', marginTop: '4px', opacity: 0.7, textAlign: 'right' }}>
                            {formatTime(msg.created_at)}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div style={{ padding: '12px 16px', borderTop: '1px solid #e9ebec', backgroundColor: 'white' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      border: '2px solid #405189',
                      borderRadius: '24px',
                      padding: '8px 12px',
                      backgroundColor: '#fff'
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={sending}
                      style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        fontSize: '14px',
                        backgroundColor: 'transparent'
                      }}
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!newMessage.trim() || sending}
                      style={{
                        background: newMessage.trim() ? '#405189' : '#e9ebec',
                        border: 'none',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: newMessage.trim() ? 'pointer' : 'default',
                        transition: 'background 0.2s'
                      }}
                    >
                      {sending ? (
                        <Spinner size="sm" color="light" />
                      ) : (
                        <i className="ri-arrow-up-line" style={{ color: newMessage.trim() ? 'white' : '#878a99', fontSize: '16px' }}></i>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Hover effect and animation styles */}
      <style>{`
        .floating-chat-btn:hover {
          transform: scale(1.1);
        }
        @keyframes pulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }
      `}</style>
    </>
  );
};

export default GuestChatWidget;
